import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

/**
 * Operator-facing error copy keyed by a mutation's `mutationKey`. The trading
 * on/off controls get an explicit "may still be ACTIVE" warning: a silently
 * dropped off-toggle is how a rule the operator believed was off kept trading
 * (Roberto Sánchez opener, 2026-06-09 — the optimistic switch painted OFF but
 * the request never reached the backend and nothing surfaced the failure).
 * Untagged mutations fall back to the generic message.
 */
export const MUTATION_ERROR_MESSAGES: Record<string, string> = {
  // Two failure modes: a precheck/404 abort writes NOTHING, or the rows are all
  // written and only the ENGINE config publish failed. The second is the
  // dangerous one — the UI reads Mongo, so a refresh shows the new values on
  // every row while the engine keeps firing the PREVIOUS config.
  'sniper:bulk-patch-markets':
    'Failed to apply the bulk change — the rows may already be saved while the ENGINE still runs the PREVIOUS config for those markets. Retry until it succeeds; refreshing will NOT show the difference.',
  // Same shape as the bulk write: the row is saved and THEN the engine config is
  // republished, so a failure after the save leaves the engine on the old config
  // while the UI (which reads Mongo) shows the new values.
  'sniper:patch-market':
    'Failed to save the market — the row may already be saved while the ENGINE still runs the PREVIOUS config for that fixture. Retry until it succeeds; refreshing will NOT show the difference.',
  // Worse than a patch: the row is deleted and THEN the config is republished, so
  // a failure after the delete leaves a market the operator can no longer see
  // still present in the engine config — where it can still fire.
  'sniper:delete-market':
    'Failed to delete the market — it may already be REMOVED from the list while the ENGINE still carries it in the previous config and CAN STILL FIRE it. Retry until it succeeds.',
  'entry:create-rule':
    'Failed to create the opener rule — it was NOT created.',
  'entry:activate':
    'Failed to activate the opener rule — it was NOT activated. Retry.',
  'entry:pause':
    'Failed to pause the opener rule — it may still be ACTIVE. Retry and confirm.',
  'entry:set-armed':
    'Failed to change the opener rule on/off — its real state may be unchanged (it could still be ACTIVE). Retry and confirm the switch.',
  'entry:set-maker-flag':
    'Failed to change the opener rule — it may still be ACTIVE. Retry and confirm the switch.',
  'entry:bulk-armed':
    'Failed to arm/disarm the selected opener rules — their real state may be unchanged (some could still be ACTIVE, others still paused). Retry and confirm.',
  'entry:bulk-set-maker-flag':
    'Failed to change the maker flag for the selected opener rules — their real state may be unchanged (some could still be ACTIVE). Retry and confirm.',
  'entry:toggle-platform':
    'Failed to toggle the opener platform — the rule may still be ACTIVE. Retry and confirm the switch.',
  'entry:update-rule-params':
    'Failed to save the opener rule parameters — the running rule may still use the OLD values. Retry and confirm.',
  'entry:bulk-params':
    'Failed to apply the bulk parameters — the opener rules may still use the OLD values. Retry.',
  'entry:set-pair-opener-enabled':
    'Failed to change the pair market data on/off — its real state may be unchanged (it could still be streaming). Retry and confirm the switch.',
  'entry:bulk-opener-enabled':
    'Failed to change market data for the pairs — their real state may be unchanged (some could still be ON, others still OFF). Retry and confirm.',
  'unwinding:pause':
    'Failed to pause the unwinder rule — it may still be ACTIVE. Retry and confirm the switch.',
  'unwinding:toggle-platform':
    'Failed to toggle the unwinder platform — the rule may still be ACTIVE. Retry and confirm the switch.',
  'unwinding:update-rule-params':
    'Failed to save the unwinder rule parameters — the running rule may still use the OLD values. Retry and confirm.',
  'unwinding:bulk-params':
    'Failed to apply the bulk parameters — the unwinder rules may still use the OLD values. Retry.',
  'unwinding:set-pair-unwinder-enabled':
    'Failed to change the pair market data on/off — its real state may be unchanged (it could still be monitored). Retry and confirm the switch.',
  'unwinding:bulk-unwinder-enabled':
    'Failed to change market data for the pairs — their real state may be unchanged (some could still be ON, others still OFF). Retry and confirm.',
  // Only the HTTP dispatch of these two lands here — a job that starts and
  // then fails a step reports through the job stepper/snackbar instead.
  'entry:start-opener':
    'Failed to start the opener — the request never reached the control plane, so NOTHING was started. Retry.',
  'entry:stop-opener':
    'Failed to stop the opener — the request never reached the control plane, so orders may still be RESTING and rules ACTIVE. Retry immediately.',
  'entry:clear-panic-lockout':
    'Failed to clear the 24h lockout — it is probably STILL armed, so starting a group or activating a rule will keep being refused. Retry and confirm.',
  'entry:group-start':
    'Failed to start the group pods — the group is NOT running.',
  'entry:group-stop':
    'Failed to stop the group — its rules may still be ACTIVE and its pods may still be running.',
  'entry:group-panic-soft':
    'Group SOFT PANIC failed — orders may still be resting and rules may still be ACTIVE.',
  'entry:group-panic':
    'Group HARD PANIC failed — the engine pod may still be running and orders may still be resting.',
  'entry:group-finish':
    'Failed to finish group — the group may NOT be finished. Retry.',
  'match-events:seed-score':
    'Failed to set the match score — the live baseline was NOT established (the scoreboard may stay syncing). Retry.',
  'positions:toggle-tna-alerts':
    'Failed to toggle TNA alerts for this position — the setting may not have changed. Retry.',
  'entry:create-subgroup':
    'Failed to create the subgroup — it was NOT created.',
  'entry:update-subgroup':
    'Failed to save the subgroup — its config snapshot was NOT changed.',
  'entry:delete-subgroup':
    'Failed to delete the subgroup — its members may still be assigned.',
  'entry:assign-pair-subgroup':
    'Failed to move the pair to the subgroup — its assignment is unchanged.',
  'entry:bulk-assign-subgroup':
    'Failed to move the selected pairs — their subgroup assignment is unchanged.',
  'entry:apply-subgroup':
    'Failed to apply the subgroup config — member rules may NOT carry the snapshot. Retry.',
  'entry:create-subgroup-template':
    'Failed to create the template — it was NOT created.',
  'entry:update-subgroup-template':
    'Failed to save the template — it was NOT changed.',
  'entry:delete-subgroup-template':
    'Failed to delete the template — it may still exist.',
  'entry:instantiate-subgroup-template':
    'Failed to instantiate the template — the group subgroups may NOT have been created.',
  'entry:save-group-default':
    'Failed to save the group default config — no snapshot was stored. Retry.',
  'entry:reset-group-default':
    'Failed to reset rules to the group default — member rules were NOT changed. Retry.',
  // A failed PANIC ALL is the worst possible silence: the operator asked for a
  // full halt and may have got a PARTIAL one (some domains armed, others not).
  // The keys may or may not have been cleared before the failure — the resume
  // clears them BEFORE the bookkeeping that can throw — so the message points
  // at the chips that read the real state instead of guessing.
  'system:venue-maintenance-resume':
    'Resume after venue maintenance FAILED — do not assume the engines came back. Check the halt chips for what is actually armed.',
  'system:panic-all':
    'PANIC ALL failed — the halt may have applied to only SOME domains, and rules elsewhere can still be ACTIVE. Retry immediately and check each domain.',
  'opener:boot-orchestrator':
    'Failed to boot the opener control plane — it is still DOWN, so opener rule changes will not reach the engine.',
  'opener:shutdown-orchestrator':
    'Failed to shut down the opener control plane — it may still be running.',
  'unwinder:boot-orchestrator':
    'Failed to boot the unwinder control plane — it is still DOWN, so unwinder rule changes will not reach the engine.',
  'unwinder:shutdown-orchestrator':
    'Failed to shut down the unwinder control plane — it may still be running.',
  'entry:soccer-matches-prepare':
    'Failed to prepare the soccer match — its buckets may be PARTIALLY assigned/enabled/ruled. Re-open the drawer and check the status chips before retrying.',
  'entry:soccer-matches-turn-on':
    'Failed to turn on the match — some buckets may be PARTIALLY prepared/armed while others were not reached. If a pair ALREADY had a rule, its maker flags may now be in an in-between state that the status chips (based on counts, not flags) will NOT show as broken. Re-open the drawer, but also check that pair\'s maker flags directly on the Entry table before retrying.',
  'entry:soccer-matches-turn-off':
    'Failed to turn off the match — some rules may still be ARMED, and a rule\'s maker flags may now be PARTIALLY cleared, a state the status chips (based on counts, not flags) will NOT show as broken. Re-open the drawer, but also check that rule\'s maker flags directly on the Entry table before retrying.'
};

export const DEFAULT_MUTATION_ERROR_MESSAGE =
  'Action failed — server state may be unchanged. Please retry.';

/**
 * Message-stem routing for the groups-only trading rejection. Activation
 * mutations (`entry:set-maker-flag`, `entry:toggle-platform`) are tagged
 * with the "may still be ACTIVE" copy — but when the backend refused the
 * activation BECAUSE the pair has no opener group, that copy is wrong (the
 * rule is definitively NOT active) and unactionable. Matching on the stable
 * stem of the backend message (not the full phrasing, which may drift)
 * routes this case to copy that tells the operator exactly what to do.
 */
export const GROUPS_ONLY_REJECTION_PATTERN =
  /not assigned to an opener group/i;

export const GROUPS_ONLY_REJECTION_MESSAGE =
  'Rule NOT activated — the pair is not assigned to an opener group; assign it in the Pairs Bucket and retry.';

/**
 * Best-effort extraction of the backend's rejection reason from an axios
 * error (NestJS error body: `message` as a string, or string[] from the
 * ValidationPipe). The generic toast used to swallow these — a precise 400
 * like "thresholdSum must be < 1.0" surfaced as an opaque "Action failed",
 * leaving the operator guessing which input the server refused.
 */
export function extractServerErrorMessage(error: unknown): string | null {
  const data = (error as { response?: { data?: unknown } } | null | undefined)
    ?.response?.data;
  if (!data || typeof data !== 'object') return null;
  const message = (data as { message?: unknown }).message;
  if (typeof message === 'string' && message.trim()) return message.trim();
  if (Array.isArray(message)) {
    const parts = message.filter(
      (m): m is string => typeof m === 'string' && m.trim().length > 0
    );
    if (parts.length) return parts.join('; ');
  }
  return null;
}

export function resolveMutationErrorMessage(
  mutationKey: unknown,
  error?: unknown
): string {
  const serverMessage = extractServerErrorMessage(error);
  // Groups-only activation rejection overrides BOTH the keyed copy and the
  // generic fallback: the keyed "may still be ACTIVE" warning would mislead
  // here — the backend refused the activation, so the rule is NOT active.
  if (serverMessage && GROUPS_ONLY_REJECTION_PATTERN.test(serverMessage)) {
    return GROUPS_ONLY_REJECTION_MESSAGE;
  }
  if (
    typeof mutationKey === 'string' &&
    Object.prototype.hasOwnProperty.call(MUTATION_ERROR_MESSAGES, mutationKey)
  ) {
    const tailored = MUTATION_ERROR_MESSAGES[mutationKey];
    return serverMessage ? `${tailored} ${serverMessage}` : tailored;
  }
  return serverMessage
    ? `Action failed — ${serverMessage}`
    : DEFAULT_MUTATION_ERROR_MESSAGE;
}

/**
 * Single source of truth for surfacing a failed react-query mutation to the
 * operator. Wired app-wide via the QueryClient's MutationCache so NO mutation
 * failure is ever silent — the optimistic-toggle rollback reverts the switch,
 * this makes the failure visible and reports it to Sentry.
 */
export function handleMutationError(error: unknown, mutationKey?: unknown): void {
  toast(resolveMutationErrorMessage(mutationKey, error), {
    theme: 'dark',
    type: 'error',
    autoClose: 8000,
    pauseOnFocusLoss: false
  });
  Sentry.captureException(error, {
    extra: {
      mutationKey: typeof mutationKey === 'string' ? mutationKey : null,
      serverMessage: extractServerErrorMessage(error)
    }
  });
}
